<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';

const store = useStore();
const { t } = useI18n();

const RANGES = [
  { key: '7', days: 7 },
  { key: '30', days: 30 },
  { key: '90', days: 90 },
];
const activeRange = ref('30');

const data = useMapGetter('aiReports/getData');
const uiFlags = useMapGetter('aiReports/getUIFlags');

function fetchReport() {
  const days = RANGES.find(r => r.key === activeRange.value).days;
  const until = Math.floor(Date.now() / 1000);
  const since = until - days * 24 * 60 * 60;
  store.dispatch('aiReports/get', { since, until });
}

watch(activeRange, fetchReport);
onMounted(fetchReport);

// ---- derived values ----
const leads = computed(() => data.value.leads);
const conversations = computed(() => data.value.conversations);
const kb = computed(() => data.value.kb);

const conversionRate = computed(() => {
  const s = leads.value.by_status || {};
  const closed =
    (s.qualified || 0) + (s.contacted || 0) + (s.won || 0) + (s.lost || 0);
  if (!closed) return '0%';
  return `${Math.round(((s.won || 0) / closed) * 100)}%`;
});

const tiles = computed(() => [
  {
    label: t('AI_REPORTS.TILES.CONVERSATIONS'),
    value: conversations.value.total || 0,
  },
  { label: t('AI_REPORTS.TILES.LEADS'), value: leads.value.total || 0 },
  {
    label: t('AI_REPORTS.TILES.PROSPECTS'),
    value: leads.value.by_category?.prospect || 0,
  },
  { label: t('AI_REPORTS.TILES.CONVERSION'), value: conversionRate.value },
  { label: t('AI_REPORTS.TILES.UNANSWERED'), value: kb.value.miss_count || 0 },
]);

// ---- chart builders ----
const COLOR = '#3b82f6';
const COLOR_SOFT = '#93c5fd';

function timeseriesChart(series, color) {
  return {
    labels: (series || []).map(p => p.date.slice(5)),
    datasets: [
      {
        data: (series || []).map(p => p.count),
        backgroundColor: color,
        label: '',
      },
    ],
  };
}

function hashChart(pairs, color) {
  return {
    labels: pairs.map(p => p.label),
    datasets: [
      { data: pairs.map(p => p.value), backgroundColor: color, label: '' },
    ],
  };
}

const conversationsChart = computed(() =>
  timeseriesChart(conversations.value.timeseries, COLOR)
);
const leadsChart = computed(() =>
  timeseriesChart(leads.value.timeseries, COLOR_SOFT)
);

const FUNNEL_ORDER = ['new_lead', 'qualified', 'contacted', 'won', 'lost'];
const funnelChart = computed(() =>
  hashChart(
    FUNNEL_ORDER.map(k => ({
      label: t(`AI_REPORTS.STATUS.${k.toUpperCase()}`),
      value: leads.value.by_status?.[k] || 0,
    })),
    COLOR
  )
);

const CATEGORY_ORDER = [
  'prospect',
  'cold_call',
  'job_seeker',
  'solicitation',
  'support',
  'other',
];
const categoryChart = computed(() =>
  hashChart(
    CATEGORY_ORDER.map(k => ({
      label: t(`AI_REPORTS.CATEGORY.${k.toUpperCase()}`),
      value: leads.value.by_category?.[k] || 0,
    })),
    COLOR_SOFT
  )
);

const serviceChart = computed(() =>
  hashChart(
    (leads.value.top_service_interests || []).map(x => ({
      label: x.name,
      value: x.count,
    })),
    COLOR
  )
);
const platformChart = computed(() =>
  hashChart(
    (leads.value.top_platforms || []).map(x => ({
      label: x.name,
      value: x.count,
    })),
    COLOR_SOFT
  )
);

const hasKbArticles = computed(() => (kb.value.top_articles || []).length > 0);
</script>

<template>
  <div
    class="flex flex-col w-full h-full gap-6 p-6 overflow-auto bg-n-background"
  >
    <header class="flex items-center justify-between">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('AI_REPORTS.HEADER') }}
      </h1>
      <select
        v-model="activeRange"
        class="h-8 py-0 pl-3 pr-8 text-sm border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12"
      >
        <option v-for="r in RANGES" :key="r.key" :value="r.key">
          {{ t('AI_REPORTS.RANGE', { days: r.days }) }}
        </option>
      </select>
    </header>

    <!-- KPI tiles -->
    <div class="grid grid-cols-2 gap-4 md:grid-cols-5">
      <div
        v-for="tile in tiles"
        :key="tile.label"
        class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <span class="text-sm text-n-slate-11">{{ tile.label }}</span>
        <span class="text-2xl font-semibold text-n-slate-12">{{
          tile.value
        }}</span>
      </div>
    </div>

    <!-- Volume over time -->
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <h3 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('AI_REPORTS.CHARTS.CONVERSATIONS') }}
        </h3>
        <div class="h-56"><BarChart :collection="conversationsChart" /></div>
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <h3 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('AI_REPORTS.CHARTS.LEADS') }}
        </h3>
        <div class="h-56"><BarChart :collection="leadsChart" /></div>
      </div>
    </div>

    <!-- Funnel + category -->
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <h3 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('AI_REPORTS.CHARTS.FUNNEL') }}
        </h3>
        <div class="h-56"><BarChart :collection="funnelChart" /></div>
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <h3 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('AI_REPORTS.CHARTS.CATEGORY') }}
        </h3>
        <div class="h-56"><BarChart :collection="categoryChart" /></div>
      </div>
    </div>

    <!-- Topics -->
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <h3 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('AI_REPORTS.CHARTS.SERVICES') }}
        </h3>
        <div class="h-56"><BarChart :collection="serviceChart" /></div>
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <h3 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('AI_REPORTS.CHARTS.PLATFORMS') }}
        </h3>
        <div class="h-56"><BarChart :collection="platformChart" /></div>
      </div>
    </div>

    <!-- KB insights -->
    <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
      <h3 class="mb-3 text-sm font-medium text-n-slate-12">
        {{ t('AI_REPORTS.CHARTS.KB') }}
      </h3>
      <p v-if="!hasKbArticles" class="text-sm text-n-slate-11">
        {{ t('AI_REPORTS.KB_EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-2">
        <li
          v-for="a in kb.top_articles"
          :key="a.slug"
          class="flex items-center justify-between text-sm text-n-slate-12"
        >
          <span>{{ a.slug }}</span>
          <span class="text-n-slate-11">{{ a.count }}</span>
        </li>
      </ul>
    </div>

    <p v-if="uiFlags.isFetching" class="text-sm text-center text-n-slate-11">
      {{ t('AI_REPORTS.LOADING') }}
    </p>
  </div>
</template>
